import { v4 as uuid } from "uuid";
export class OscillatorInstrument {
    constructor(context, settings = OscillatorInstrument.createDefaults()) {
        this._settings = settings;
        this.id = uuid();
        this._context = context;
        this._masterGain = context.createGain();
        this._masterGain.gain.value = this._settings.gain;
        this._sources = [OscillatorInstrument.newOscillator(context, settings, this._masterGain)];
    }
    get oscillatorType() {
        return this._sources[0].oscillator.type;
    }
    set oscillatorType(value) {
        if (["sine", "square", "sawtooth", "triangle"].indexOf(value) == -1) {
            throw new Error("Invalid oscillator type");
        }
        this._settings.oscillatorType = value;
        for (let i = 0; i < this._sources.length; i++) {
            this._sources[i].oscillator.type = value;
        }
    }
    get masterGain() {
        return this._masterGain.gain.value;
    }
    set masterGain(value) {
        if (!(value >= 0 && value <= 1)) {
            throw new RangeError("Invalid Gain Value");
        }
        this._settings.gain = value;
        this._masterGain.gain.setValueAtTime(value, this._context.currentTime);
    }
    get envelopeEnabled() {
        return this._settings.envelopeEnabled;
    }
    set envelopeEnabled(value) {
        this._settings.envelopeEnabled = value;
    }
    get envelope() {
        return this._settings.envelope;
    }
    set envelope(value) {
        this._settings.envelope = value;
    }
    connect(node) {
        if (node instanceof AudioNode) {
            this._masterGain.connect(node);
        }
        else {
            this._masterGain.connect(node.input);
        }
    }
    disconnect(node) {
        if (node instanceof AudioNode) {
            this._masterGain.disconnect(node);
        }
        else {
            this._masterGain.disconnect(node.input);
        }
    }
    disconnectAll() {
        this._masterGain.disconnect();
    }
    playNote(start, end, frequency) {
        let sourceToUse = null;
        for (let i = 0; i < this._sources.length; i++) {
            let sourceAvailable = true;
            let currentSource = this._sources[i];
            for (let j = currentSource.usage.length - 1; j >= 0; j--) {
                let noteDuration = currentSource.usage[j];
                if (this._context.currentTime > noteDuration[1]) {
                    currentSource.usage.splice(j, 1);
                    continue;
                }
                else if (start <= noteDuration[1]) {
                    sourceAvailable = false;
                    break;
                }
            }
            if (sourceAvailable) {
                sourceToUse = currentSource;
                break;
            }
        }
        ;
        if (sourceToUse === null) {
            sourceToUse = OscillatorInstrument.newOscillator(this._context, this._settings, this._masterGain);
            sourceToUse.oscillator.frequency.setValueAtTime(frequency, 0);
            this._sources.push(sourceToUse);
        }
        sourceToUse.usage.push([start, end]);
        this.scheduleNote(start, frequency, end, sourceToUse.oscillator, sourceToUse.gain);
    }
    scheduleNote(startTime, frequency, endTime, source, sourceGain, volume = 0.999) {
        if (startTime < this._context.currentTime) {
            startTime = this._context.currentTime;
        }
        source.frequency.setValueAtTime(frequency, startTime);
        let attackEnd = startTime + 0.001;
        let releaseStart = endTime - 0.001;
        if (this._settings.envelopeEnabled) {
            releaseStart = endTime - this._settings.envelope.release;
            attackEnd = startTime + this._settings.envelope.attack;
        }
        releaseStart = Math.max(endTime - this._settings.envelope.release, startTime);
        attackEnd = Math.min(startTime + this._settings.envelope.attack, endTime);
        if (attackEnd > releaseStart) {
            attackEnd = releaseStart;
        }
        sourceGain.gain.cancelScheduledValues(startTime);
        sourceGain.gain.setTargetAtTime(0.0001, startTime, 0.01);
        sourceGain.gain.linearRampToValueAtTime(volume, attackEnd);
        sourceGain.gain.cancelScheduledValues(releaseStart);
        sourceGain.gain.setValueAtTime(volume, releaseStart);
        sourceGain.gain.exponentialRampToValueAtTime(0.00001, endTime);
    }
    stop() {
        this._sources.forEach(element => {
            element.gain.gain.cancelScheduledValues(this._context.currentTime);
            element.gain.gain.setTargetAtTime(0.0001, this._context.currentTime, 0.03);
        });
    }
    serialise() {
        return this._settings;
    }
    destroy() {
        this._masterGain = null;
        this._sources = [];
    }
    static newOscillator(context, settings, destination) {
        let source = context.createOscillator();
        source.start();
        source.type = settings.oscillatorType;
        let sourceGain = context.createGain();
        sourceGain.gain.value = 0;
        source.connect(sourceGain);
        sourceGain.connect(destination);
        return { oscillator: source, gain: sourceGain, usage: [] };
    }
    static createDefaults() {
        return {
            "type": "oscillator",
            "oscillatorType": "sine",
            "gain": 0.5,
            "envelopeEnabled": true,
            "envelope": {
                "attack": 0.1,
                "release": 0.1
            }
        };
    }
}
//# sourceMappingURL=OscillatorInstrument.js.map